import React from "react";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface PaymentCard {
  id: string;
  type: 'visa' | 'mastercard' | 'amex' | 'discover';
  lastFour: string;
  expiryDate: string;
  isDefault: boolean;
}

interface PaymentMethodsTabProps {
  onAddPaymentClick: () => void;
}

const PaymentMethodsTab: React.FC<PaymentMethodsTabProps> = ({ onAddPaymentClick }) => {
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const { t } = useTranslation("settings");

  // Sample payment cards data
  const paymentCards: PaymentCard[] = [
    {
      id: "1",
      type: "visa",
      lastFour: "9267",
      expiryDate: "12/26",
      isDefault: true
    },
    {
      id: "2",
      type: "mastercard",
      lastFour: "4521",
      expiryDate: "08/27",
      isDefault: false
    }
  ];

  const getCardIcon = (cardType: PaymentCard['type']) => {
    const cardIcons = {
      visa: "fa-cc-visa",
      mastercard: "fa-cc-mastercard", 
      amex: "fa-cc-amex",
      discover: "fa-cc-discover"
    };
    
    return cardIcons[cardType] || "fa-credit-card";
  };

  const getCardName = (cardType: PaymentCard['type']) => {
    const cardNames = {
      visa: t("billing.paymentMethods.cardTypes.visa"),
      mastercard: t("billing.paymentMethods.cardTypes.mastercard"),
      amex: t("billing.paymentMethods.cardTypes.amex"), 
      discover: t("billing.paymentMethods.cardTypes.discover")
    };
    
    return cardNames[cardType] || t("billing.paymentMethods.cardTypes.card");
  };
  
  const getMaskedCardNumber = (lastFour: string) => {
    return `•••• ${lastFour}`;
  };

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === 'member') {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadBillingPaymentMethods = canAccessMenu('read_billing_payment_methods');
  const canCreateBillingPaymentMethods = canAccessMenu('create_billing_payment_methods');
  const canEditBillingPaymentMethods = canAccessMenu('edit_billing_payment_methods');
  const canDeleteBillingPaymentMethods = canAccessMenu('delete_billing_payment_methods');

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === 'member' && !canReadBillingPaymentMethods) {
    return null;
  }

  return (
    <div className="billing-tab-content">
      <div className="billing-payment-section">
        <div className="payment-methods-header">
          <h3>{t("billing.paymentMethods.title")}</h3>
            <button className="btn-preview" onClick={onAddPaymentClick}>
              <i className="fa fa-plus"></i>
              {t("billing.paymentMethods.addButton")}
            </button>
        </div>
        
        <div className="payment-methods-list">
          {paymentCards.map((card) => (
            <div key={card.id} className="payment-method-card">
              <div className="payment-method-info">
                <div className="payment-method-icon">
                  <i className={`fa ${getCardIcon(card.type)} card-brand-icon`}></i>
                </div>
                <div className="payment-method-details">
                  <h4>{getCardName(card.type)} {getMaskedCardNumber(card.lastFour)}</h4>
                  <p>{t("billing.paymentMethods.expires")} {card.expiryDate}</p>
                </div>
              </div>
              <div className="payment-method-actions">
                {card.isDefault && <span className="payment-method-default">{t("billing.paymentMethods.default")}</span>}
                {canDeleteBillingPaymentMethods && (
                  <button className="payment-method-btn" title={t("billing.paymentMethods.deleteCard")}>
                    <i className="fa fa-trash"></i>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsTab;